import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useParams } from 'react-router-dom';
import useUserIdentity from '../hooks/useUserIdentity';
// import useClock from '../hooks/useClock';

import { Device, Call as TwilioCall } from '@twilio/voice-sdk';
import { Call } from '../stores/call_store';
import { CallParticipant } from '../stores/call_participant_store';
import useStores from '../hooks/useStores';

// import { observer } from 'mobx-react-lite';

import CallAPI from '../api/call_api';

export type PhoneContextType = {
    phoneSetup: (identityKeyOnBehalf: string, collabKey: string) => void;
    phoneMakeCall: (number: string, associated_type: string, associated_uuid: string) => Promise<any>;
    phoneAddParticipant: (number: string, callKey: string, identityKey: string) => Promise<any>;
    phoneRemoveParticipant: (callKey: string, call_sid: string) => Promise<any>;
    getCall: (callKey: string) => Call | null;
    getCallParticipants: (callKey: string) => CallParticipant[];
    fetchCallParticipants: (callKey: string, identityKey: string) => Promise<any>;
    phoneHangUp: () => void;
    activeCallKey: string | null;
    phoneReady: boolean;
    fetchCallsByAssociation: (assocated_type: string, assocated_uuid: string, identityKey: string) => void;
    getCallsByAssociation: (assocated_type: string, assocated_uuid: string) => Call[];
    sendDTMF: (tone: string) => void;
}

export const PhoneContext = React.createContext<PhoneContextType>({
    phoneSetup: () => { throw new Error('not implemented');},
    phoneMakeCall: () => { throw new Error('not implemented');},
    phoneHangUp: () => { throw new Error('not implemented');},
    phoneAddParticipant: () => { throw new Error('not implemented');},
    phoneRemoveParticipant: () => { throw new Error('not implemented');},
    phoneReady: false,
    activeCallKey: null,
    getCall: () => { throw new Error('not implemented');},
    getCallParticipants: () => { throw new Error('not implemented');},
    fetchCallParticipants: () => { throw new Error('not implemented');},
    fetchCallsByAssociation: () => { throw new Error('not implemented');},
    getCallsByAssociation: () => { throw new Error('not implemented');},
    sendDTMF: () => { throw new Error('not implemented');}
    //    collabCalls: [] as Call[]
});

export const PhoneProvider = function({ children }: { children: React.ReactNode })  {
    const [phoneReady, setPhoneReady] = useState(false);
    const [loaded, setLoaded] = useState(false);
    //    const [collabCalls, setCollabCalls] = useState([] as Call[]);
    const [twilioToken, setTwilioToken] = useState<string | null>(null);
    const [activeCallKey, setActiveCallKey] = useState<string | null>(null);
    const [twilioCall, setTwilioCall] = useState<TwilioCall | null>(null);
    //    const [knownParticipants, setKnownParticipants] = useState([] as CallParticipant[]);
    const { currentIdentity } = useUserIdentity();

    // const { currentCollab } = useCollabs();

    const { callStore, callParticipantStore  } = useStores();

    const device = useMemo(() => {
        if(!twilioToken) {
            return null;
        }
        return new Device(twilioToken)
    }, [twilioToken]);


    const getToken = useCallback(() => {
        if(!loaded) {
            if(currentIdentity?.uuid) {
                setLoaded(true);
                CallAPI.getToken(currentIdentity.uuid).then((data) => {
                    setTwilioToken(data.jwt);
                });
            }
        }
    }, [currentIdentity.uuid, loaded, setLoaded, setTwilioToken]);


    const setupDevice = useCallback(() => {
        if(device) {
            device.addListener('registered', () => {
                console.log('The device is ready to receive incoming calls.')
            });

            device.on('incoming', (call) => {
                console.log("incoming call %o", call);
                // Do something
            });

            device.on('disconnect', (conn) => {
                console.log("dis connect %o", conn);
                setTwilioCall(null);
                // Do something
            });

            device.on('tokenWillExpire', () => {
                console.error("Tokenwillexpire");
                // const token = getNewTokenViaAjax();
                // device.updateToken(token);
            });
        }
    }, [device]);


    const setupPhoneReady = useCallback(() => {
        if(device) {
            setPhoneReady(true);
        }
        else {
            setActiveCallKey(null);
            setPhoneReady(false);
        }
    }, [device, setPhoneReady]);

    useEffect(() => {
        getToken();
    }, [getToken]);

    useEffect(() => {
        setupDevice();
    }, [setupDevice]);

    useEffect(() => {
        setupPhoneReady();
    }, [setupPhoneReady]);


    function phoneSetup() {
        console.log("phone setup, with device: %o", device);
        setPhoneReady(true);
    }


    function sendDTMF(tone: string) {
        console.log("ok, trying to send dtmf to %s", twilioCall);
        if(!twilioCall) {
            return;
        }
    twilioCall.sendDigits(tone);
}

    function getCallParticipants(callKey: string) {
        if(!callKey) {
            return [];
        }
        return callParticipantStore.items.filter((p) => p.call == callKey);
    }

    function getCall(callKey: string) {
        if(!callKey) {
            return null;
        }
        return callStore.items.find((c) => c.key == callKey) || null;
    }

    function fetchCallParticipants(callKey: string, identityKey: string) {
        return callParticipantStore.fetchParticipants(callKey, identityKey);
    }

    async function phoneMakeCall(numberTo: string, associated_type: string, associated_uuid: string) {
        if(!(currentIdentity?.uuid && device)) {
            return Promise.reject("no identity");
        }

        return CallAPI.createCall(numberTo,
            associated_type,
            associated_uuid,
            currentIdentity?.uuid)
            .then(async (data) => {
                callStore.receiveUpdate(data);
                const callKey = data.key;
                device.connect({ params: { To: numberTo, call: callKey } })
                    .then((call) => {
                        setActiveCallKey(callKey);
                        setTwilioCall(call);
                        setTimeout(() => {
                            phoneAddParticipant(numberTo, callKey, currentIdentity?.uuid);
                        }, 4000);
                        // add to clock log
                    });

            });
    }

    function phoneHangUp() {
        if(device) {
            return device.disconnectAll();
        }
        setActiveCallKey(null);
    }

    function fetchCallsByAssociation(associated_type: string, associated_uuid: string, identityKey: string) {
        return callStore.fetchCallsByAssociation(associated_type, associated_uuid, identityKey);
    }


    function getCallsByAssociation(associated_type: string, associated_uuid: string) {
        return callStore.items.filter((c => c.associated_uuid == associated_uuid && c.associated_type == associated_type));
    }

    function phoneAddParticipant(numberTo: string, callKey: string, identityKey: string) {
        if(!device)
            return Promise.reject("no device");

        return CallAPI.addParticipant(numberTo,
            callKey,
            identityKey)
            .then((data) => {
                callParticipantStore.receiveUpdate(data);
            });
    }


    function phoneRemoveParticipant(callKey: string, call_sid: string) {
        return CallAPI.removeParticipant(callKey, call_sid, currentIdentity.uuid);
    }

    const data = {
        phoneSetup: phoneSetup,
        phoneReady: phoneReady,
        phoneMakeCall: phoneMakeCall,
        phoneHangUp: phoneHangUp,
        phoneAddParticipant: phoneAddParticipant,
        phoneRemoveParticipant: phoneRemoveParticipant,
        getCall: getCall,
        getCallParticipants: getCallParticipants,
        fetchCallParticipants: fetchCallParticipants,

        fetchCallsByAssociation,
        getCallsByAssociation,

        activeCallKey,
        sendDTMF,
    };

    return (
        <PhoneContext.Provider value={data}>{ children }</PhoneContext.Provider>
    );

};
